import BarRadioChart from "@/modules/QuestionWizard/QuestionTypes/BarRadioChart";
import MultiSelect from "@/modules/QuestionWizard/QuestionTypes/MultiSelect";
import SingleSelect from "@/modules/QuestionWizard/QuestionTypes/SingleSelect";
import Slider from "@/modules/QuestionWizard/QuestionTypes/Slider";
import { useAppContext } from "@/hooks/useAppContext";
import { QuestionAnswer } from "@/types/questions";
import BubbleSelect from "@/modules/QuestionWizard/QuestionTypes/BubbleSelect";
import Container from "@/components/Container";
import ProgressBar from "@/components/ProgressBar";
import { trackCompleteStep } from "@/utils/adobeAnalytics";

const QUESTION_TYPES = {
  BAR_RADIO: "bar_radio_chart",
  MULTI: "multi_select",
  SINGLE: "single_select",
  BUBBLE: "bubble_select",
  SLIDER: "slider",
  INTERSTITIAL: "interstitial",
};

type Variants =
  | "variant1"
  | "variant2"
  | "variant3"
  | "variant4"
  | "variant5"
  | "variant6";

const QuestionWizard = () => {
  const {
    activeStep,
    setActiveStep,
    questions,
    answers,
    setAnswers,
    setInterstitialVariant,
  } = useAppContext();

  const numberOfQuestions = questions.length;
  const startingProgress = 100 / numberOfQuestions;
  const progress = startingProgress * activeStep;

  const updateAnswer = (answer: QuestionAnswer | QuestionAnswer[]) => {
    const currentStep = activeStep;
    if (!Array.isArray(answer)) {
      if (answer.interstitialVariant) {
        setInterstitialVariant(answer.interstitialVariant as Variants);
      }
      setAnswers({
        ...answers,
        [currentStep]: [answer],
      });
    } else {
      setAnswers({
        ...answers,
        [currentStep]: answer,
      });
    }

    window.scrollTo(0, 0);
  };

  const handleNextStep = (answer: QuestionAnswer | QuestionAnswer[]) => {
    const currentStep = activeStep;
    updateAnswer(answer);

    trackCompleteStep(currentStep, questions, answer);

    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
  };

  const handlePreviousStep = (
    answer: QuestionAnswer | QuestionAnswer[] | null
  ) => {
    if (answer) {
      updateAnswer(answer);
    }

    const previousStep = activeStep - 1;
    setActiveStep(previousStep);
  };

  return (
    <Container>
      <ProgressBar progress={progress} />
      {questions.map((q, index) => {
        if (q.step !== activeStep) return <></>;

        /*         if ("variants" in q) {
          setActiveStep(activeStep + 1);
          return (
            <Interstitial
              key={index}
              onBack={handlePreviousStep}
              onNext={handleNextStep}
              variant={q.variants[interstitialVariant]}
            />
          );
        } */

        switch (q.type) {
          case QUESTION_TYPES.BAR_RADIO:
            return (
              <BarRadioChart
                key={index}
                defaultSelectedAnswers={
                  answers[activeStep] ? answers[activeStep][0] : null
                }
                onBack={handlePreviousStep}
                onNext={handleNextStep}
                questionText={q.question ?? ""}
                answers={q.answers ?? []}
              />
            );
          case QUESTION_TYPES.SINGLE:
            return (
              <SingleSelect
                key={index}
                defaultSelectedAnswers={
                  answers[activeStep] ? answers[activeStep][0] : null
                }
                onBack={handlePreviousStep}
                onNext={handleNextStep}
                questionText={q.question ?? ""}
                answers={q.answers ?? []}
                logic={q.logic}
              />
            );
          case QUESTION_TYPES.MULTI:
            return (
              <MultiSelect
                key={index}
                defaultSelectedAnswers={answers[activeStep] || []}
                onBack={handlePreviousStep}
                onNext={handleNextStep}
                questionText={q.question ?? ""}
                questionSubText={q.subtitle}
                answers={q.answers ?? []}
                logic={q.logic}
              />
            );
          case QUESTION_TYPES.BUBBLE:
            return (
              <BubbleSelect
                key={index}
                defaultSelectedAnswers={
                  answers[activeStep] ? answers[activeStep][0] : null
                }
                onBack={handlePreviousStep}
                onNext={handleNextStep}
                questionText={q.question ?? ""}
                answers={q.answers ?? []}
              />
            );
          case QUESTION_TYPES.SLIDER:
            return (
              <Slider
                key={index}
                defaultSelectedAnswers={
                  answers[activeStep] ? answers[activeStep][0] : null
                }
                onBack={handlePreviousStep}
                onNext={handleNextStep}
                questionText={q.question ?? ""}
                questionSubText={q.subtitle}
                answers={q.answers ?? []}
              />
            );
        }
      })}
    </Container>
  );
};

export default QuestionWizard;
